import React, { useState } from 'react';
import { X, TrendingUp, Circle, Trophy, Dumbbell, Star, Clapperboard, Wifi, Search, Smartphone, Bot, FlaskConical, Landmark, Lightbulb, Brain, GraduationCap, Music, Film, Gamepad2, Palette, Shirt, Heart, Plane, Globe, Rocket, Mountain, PawPrint, Waves, UtensilsCrossed, Stethoscope, Flame, Car, Shield } from 'lucide-react';
import { INTEREST_OPTIONS, CATEGORY_GROUPS, getCategoriesByGroup } from '../data/categories';
import { ReadingStats } from '../types';
import { StatsPanel } from './StatsPanel';

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string; strokeWidth?: number }>> = {
  TrendingUp, Circle, Trophy, Dumbbell, Star, Clapperboard, Wifi, Search, Smartphone, Bot, FlaskConical, Landmark, Lightbulb, Brain, GraduationCap, Music, Film, Gamepad2, Palette, Shirt, Heart, Plane, Globe, Rocket, Mountain, PawPrint, Waves, UtensilsCrossed, Stethoscope, Flame, Car, Shield
};

interface ProfileSettingsProps {
  currentInterests: string[];
  onClose: () => void;
  onSave: (interests: string[]) => void;
  stats?: ReadingStats;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  currentInterests,
  onClose,
  onSave,
  stats
}) => {
  const initialInterests = Array.isArray(currentInterests) ? currentInterests : [];
  const [selectedInterests, setSelectedInterests] = useState<string[]>(initialInterests);
  const grouped = getCategoriesByGroup();

  const toggleInterest = (id: string) => {
    setSelectedInterests(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSave = () => {
    const interests = selectedInterests.length > 0 ? selectedInterests : INTEREST_OPTIONS.map(o => o.id);
    onSave(interests);
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black flex flex-col">
      <div className="flex items-center justify-between px-6 pt-8 pb-5">
        <div>
          <h2 className="text-[28px] font-bold text-white leading-tight">Your Interests</h2>
          <p className="text-white/35 text-[15px] mt-1 font-normal">
            {selectedInterests.length} selected
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white/50 active:scale-95 transition-transform"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-36">
        <div className="max-w-lg mx-auto">
          {stats && <StatsPanel stats={stats} />}

          <div className="space-y-7">
            {CATEGORY_GROUPS.map(group => (
              <div key={group}>
                <p className="text-[13px] font-medium text-white/25 mb-3">{group}</p>
                <div className="flex flex-wrap gap-2">
                  {grouped[group].map(option => {
                    const isSelected = selectedInterests.includes(option.id);
                    const Icon = ICON_MAP[option.icon];
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleInterest(option.id)}
                        className={`
                          inline-flex items-center gap-2 pl-3.5 pr-4 py-2.5 rounded-full transition-all duration-150 active:scale-[0.96]
                          ${isSelected
                            ? 'bg-white text-black'
                            : 'bg-white/[0.06] text-white/50'
                          }
                        `}
                      >
                        {Icon && <Icon size={15} strokeWidth={isSelected ? 2.2 : 1.8} />}
                        <span className="text-[13px] font-medium whitespace-nowrap">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/95 to-transparent pt-12 space-y-2.5">
        <button
          onClick={handleSave}
          disabled={selectedInterests.length === 0}
          className="w-full py-[14px] bg-white text-black rounded-full font-semibold text-[15px] active:scale-[0.98] transition-transform disabled:opacity-40"
        >
          Save
        </button>
        <button
          onClick={() => setSelectedInterests(INTEREST_OPTIONS.map(o => o.id))}
          className="w-full py-3 text-white/30 text-[13px] font-medium active:text-white/50 transition-colors"
        >
          Select All
        </button>
      </div>
    </div>
  );
};
